import { useState, useEffect, useImperativeHandle, forwardRef } from "react";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { Plus, Trash2, HelpCircle } from "lucide-react";
import { useToast } from "@/hooks/use-toast";

const REDUCTION_TIMING_OPTIONS = [
  { value: "birthday", label: "On Birthday" },
  { value: "plan_anniversary", label: "Plan Anniversary" },
  { value: "first_of_birthday_month", label: "First of Birthday Month" },
  { value: "first_of_month_following_birthday", label: "First of Month Following Birthday" },
  { value: "first_of_year", label: "First of Year" },
];

const SPOUSE_AGE_BASE_OPTIONS = [
  { value: "employee", label: "Employee Age" },
  { value: "spouse", label: "Spouse Age" },
  { value: "na", label: "Not Applicable" },
];

function FieldLabel({ tooltip, children }: { tooltip: string; children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-1.5 mb-1">
      <Label className="mb-0">{children}</Label>
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <HelpCircle className="h-3.5 w-3.5 text-muted-foreground cursor-help" />
          </TooltipTrigger>
          <TooltipContent side="top" className="max-w-xs text-sm">
            {tooltip}
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );
}

export interface AgeReductionTabHandle {
  saveSchedules: () => Promise<void>;
}

interface AgeReductionTabProps {
  formData: Record<string, any>;
  updateField: (field: string, value: any) => void;
  planType: string;
  planId: number;
}

export const AgeReductionTab = forwardRef<AgeReductionTabHandle, AgeReductionTabProps>(function AgeReductionTab({ formData, updateField, planType, planId }, ref) {
  const { toast } = useToast();
  const [ageReductions, setAgeReductions] = useState<Array<{ id?: number; ageThreshold: number; reductionPercentage: number }>>([]);
  const [loadingReductions, setLoadingReductions] = useState(false);

  useEffect(() => {
    fetch(`/api/plans/${planId}/age-reduction-schedules`, { credentials: "include" })
      .then(res => res.ok ? res.json() : [])
      .then(data => { if (Array.isArray(data)) setAgeReductions(data); })
      .catch(() => {});
  }, [planId]);

  const addAgeReduction = () => {
    setAgeReductions(prev => [...prev, { ageThreshold: 65, reductionPercentage: 35 }]);
  };

  const removeAgeReduction = (index: number) => {
    setAgeReductions(prev => prev.filter((_, i) => i !== index));
  };

  const updateAgeReduction = (index: number, field: string, value: number) => {
    setAgeReductions(prev => prev.map((item, i) => i === index ? { ...item, [field]: value } : item));
  };

  const applyStandardPreset = () => {
    setAgeReductions([
      { ageThreshold: 65, reductionPercentage: 35 },
      { ageThreshold: 70, reductionPercentage: 50 },
      { ageThreshold: 75, reductionPercentage: 75 },
    ]);
  };

  const saveAgeReductions = async () => {
    const res = await fetch(`/api/plans/${planId}/age-reduction-schedules`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ schedules: ageReductions }),
    });
    if (!res.ok) throw new Error("Failed to save age reduction schedules");
    const saved = await res.json();
    if (Array.isArray(saved)) setAgeReductions(saved);
  };

  useImperativeHandle(ref, () => ({
    saveSchedules: saveAgeReductions,
  }));

  return (
    <div className="space-y-6">
      {/* Reduction Timing & Spouse Age Base */}
      <div className="grid grid-cols-2 gap-4">
        <div>
          <FieldLabel tooltip="When age-based coverage reductions take effect — on the employee's birthday, plan anniversary, or start of the month/year.">Reduction Timing</FieldLabel>
          <Select
            value={formData.reductionTiming || ""}
            onValueChange={(value) => updateField("reductionTiming", value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select when reductions apply" />
            </SelectTrigger>
            <SelectContent>
              {REDUCTION_TIMING_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mt-1">
            When age-based reductions take effect
          </p>
        </div>
        <div>
          <FieldLabel tooltip="Whether spouse/dependent life coverage reductions are based on the employee's age or the spouse's own age.">Spouse Age Base</FieldLabel>
          <Select
            value={formData.spouseAgeBase || ""}
            onValueChange={(value) => updateField("spouseAgeBase", value)}
          >
            <SelectTrigger>
              <SelectValue placeholder="Select age basis for spouse" />
            </SelectTrigger>
            <SelectContent>
              {SPOUSE_AGE_BASE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground mt-1">
            Whose age determines spouse coverage reduction
          </p>
        </div>
      </div>

      {/* Age Reduction Schedule Table */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <Label className="text-base font-medium">Age Reduction Schedule</Label>
          <div className="flex gap-2">
            <Button type="button" variant="outline" size="sm" onClick={applyStandardPreset}>
              Standard 65/70/75
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={addAgeReduction}>
              <Plus className="h-4 w-4 mr-1" /> Add Rule
            </Button>
          </div>
        </div>

        {ageReductions.length === 0 ? (
          <p className="text-sm text-muted-foreground text-center py-4">
            No age reduction rules configured. Coverage remains unchanged regardless of age.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Age Threshold</TableHead>
                <TableHead>Reduction %</TableHead>
                <TableHead>Resulting Coverage</TableHead>
                <TableHead className="w-[60px]"></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ageReductions.map((rule, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <Input
                      type="number"
                      value={rule.ageThreshold}
                      onChange={(e) => updateAgeReduction(index, "ageThreshold", Number(e.target.value))}
                      min={50}
                      max={99}
                      className="w-24"
                    />
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Input
                        type="number"
                        value={rule.reductionPercentage}
                        onChange={(e) => updateAgeReduction(index, "reductionPercentage", Number(e.target.value))}
                        min={0}
                        max={100}
                        className="w-24"
                      />
                      <span className="text-sm text-muted-foreground">%</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    {100 - rule.reductionPercentage}% of original
                  </TableCell>
                  <TableCell>
                    <Button type="button" variant="ghost" size="sm" onClick={() => removeAgeReduction(index)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
});
